import {useParams} from 'react-router-dom';

import {DashboardCard, Header, Pagination} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {dashCardConfig} from '@/config/menuConfig';

import TransactionSearch from './TransactionSearch';
import TransactionTableContent from './TransactionTableContent';
import useUserTransactionList from './useUserTransactionList';

const UserTransactionList = () => {
  const {userId} = useParams();
  const {
    error,
    isLoading,
    isSearch,
    isPageReset,
    fetchTransactionList,
    pageInfo,
    transactionList,
    exportFilters,
    filterCount,
    currentFilters,
    filtersToApply,
    handleApplyFilters,
    clearFilters,
    selectCurrentFilters,
    selectAppliedFilters,
  } = useUserTransactionList(userId || '');
  const transactionCount = pageInfo.total > 0 ? pageInfo.total : 0;
  const debits = pageInfo.summary ? pageInfo.summary.totalDebitCount : '-';
  const debitValue = pageInfo.summary ? pageInfo.summary.totalDebitAmount : '-';
  const credits = pageInfo.summary ? pageInfo.summary.totalCreditCount : '-';
  const creditValue = pageInfo.summary
    ? pageInfo.summary.totalCreditAmount
    : '-';

  // The 'count' branch is a substring test while the rest are exact matches, so
  // it has to stay ahead of them. Falls through to the debit count by default.
  const resolveCardValue = (label: string) => {
    if (label.includes('count')) {
      return transactionCount;
    }
    if (label === 'debit value') {
      return debitValue;
    }
    if (label === 'credit value') {
      return creditValue;
    }
    if (label === 'credits') {
      return credits;
    }
    return debits;
  };

  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <Header title={`User Transactions`} />
      <TransactionSearch
        transactions={transactionList}
        exportFilters={exportFilters}
        filterCount={filterCount}
        currentFilters={currentFilters}
        filtersToApply={filtersToApply}
        clearFilters={clearFilters}
        selectCurrentFilters={selectCurrentFilters}
        selectAppliedFilters={selectAppliedFilters}
        handleApplyFilters={handleApplyFilters}
      />

      {/* Transaction Cards Section */}
      <section className="flex flex-wrap p-3 pl-0 gap-4 mt-2">
        {dashCardConfig.map((content, idx) => {
          const label = content.label.toLocaleLowerCase();
          const value = resolveCardValue(label);
          return (
            <DashboardCard
              key={idx}
              label={content.label}
              value={value}
              valueType={content.valueType}
            />
          );
        })}
      </section>

      <section className="pt-5 md:pt-5">
        <TransactionTableContent
          error={error}
          isLoading={isLoading}
          items={transactionList}
          onRetry={() => fetchTransactionList({})}
        />
      </section>

      {transactionList && transactionList.length ? (
        <Pagination
          onPageChange={fetchTransactionList as PaginationProps['onPageChange']}
          total={pageInfo.total}
          isSearch={isSearch}
          isPageReset={isPageReset}
        />
      ) : null}
    </main>
  );
};

export default UserTransactionList;
